import * as THREE from "three";
import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { ViewPreset } from "../../types/dental";

export function getViewportBounds(objects: THREE.Object3D[]): THREE.Box3 {
  const combined = new THREE.Box3();
  let hasAny = false;

  for (const obj of objects) {
    if (!obj) continue;

    obj.updateWorldMatrix(true, true);

    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) continue;

    if (!hasAny) {
      combined.copy(box);
      hasAny = true;
    } else {
      combined.union(box);
    }
  }

  return combined;
}

export function fitToView(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  objects: THREE.Object3D[],
  padding = 1.2,
  directionOverride?: THREE.Vector3
) {
  const box = getViewportBounds(objects);
  if (box.isEmpty()) {
    console.warn("fitToView: empty bounds");
    return;
  }

  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  console.log("[viewportCameraUtils] fitToView INPUT", {
  center: center.toArray(),
  size: size.toArray(),
  cameraPositionBefore: camera.position.toArray(),
  targetBefore: controls.target.toArray(),
  distanceBefore: camera.position.distanceTo(controls.target),
  directionOverride: directionOverride?.toArray(),
});

  const maxSize = Math.max(size.x, size.y, size.z, 1);
  const fov = THREE.MathUtils.degToRad(camera.fov);

  const fitHeightDistance = maxSize / (2 * Math.tan(fov / 2));
  const fitWidthDistance = fitHeightDistance / Math.max(camera.aspect, 0.0001);

  let distance = Math.max(fitHeightDistance, fitWidthDistance);
  distance *= padding;

  const currentDirection = directionOverride
    ? directionOverride.clone()
    : new THREE.Vector3().subVectors(camera.position, controls.target);

  if (currentDirection.lengthSq() < 0.0001) {
    currentDirection.set(0, 0, 1);
  }

  currentDirection.normalize();

  controls.target.copy(center);
  camera.position.copy(
    center.clone().add(currentDirection.multiplyScalar(distance))
  );

  camera.near = Math.max(distance / 100, 0.001);
  camera.far = Math.max(distance * 50, 1000);

  camera.updateProjectionMatrix();
  controls.update();

  console.log("[viewportCameraUtils] fitToView OUTPUT", {
  cameraPositionAfter: camera.position.toArray(),
  targetAfter: controls.target.toArray(),
  distanceAfter: camera.position.distanceTo(controls.target),
});
}

export function zoomByFactor(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  factor: number,
  minDistance = 0.01,
  maxDistance = 5000
) {
  const offset = new THREE.Vector3().subVectors(
    camera.position,
    controls.target
  );

  let distance = offset.length();
  if (distance < 0.0001) {
    offset.set(0, 0, 1);
    distance = 1;
  }

  offset.normalize();

  const nextDistance = THREE.MathUtils.clamp(
    distance * factor,
    minDistance,
    maxDistance
  );

  camera.position.copy(
    controls.target.clone().add(offset.multiplyScalar(nextDistance))
  );

  camera.near = Math.max(nextDistance / 100, 0.001);
  camera.far = Math.max(nextDistance * 50, 1000);

  camera.updateProjectionMatrix();
  controls.update();
}

export function setViewPreset(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  objects: THREE.Object3D[],
  preset: ViewPreset
) {
  const box = getViewportBounds(objects);
  if (box.isEmpty()) {
    console.warn("setViewPreset: empty bounds");
    return;
  }

  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  console.log("[viewportCameraUtils] setViewPreset INPUT", {
  preset,
  center: center.toArray(),
  size: size.toArray(),
  cameraPositionBefore: camera.position.toArray(),
  targetBefore: controls.target.toArray(),
  distanceBefore: camera.position.distanceTo(controls.target),
});

const maxSize = Math.max(size.x, size.y, size.z, 1);
const currentDistance = camera.position.distanceTo(controls.target);
const distance = currentDistance > 0.001 ? currentDistance : maxSize * 2;

  const pos = new THREE.Vector3();

  switch (preset) {
    case "front":
      pos.set(center.x, center.y, center.z + distance);
      camera.up.set(0, 1, 0);
      break;

    case "back":
      pos.set(center.x, center.y, center.z - distance);
      camera.up.set(0, 1, 0);
      break;

    case "left":
      pos.set(center.x - distance, center.y, center.z);
      camera.up.set(0, 1, 0);
      break;

    case "right":
      pos.set(center.x + distance, center.y, center.z);
      camera.up.set(0, 1, 0);
      break;

    case "top":
      pos.set(center.x, center.y + distance, center.z);
      camera.up.set(0, 0, -1);
      break;

    case "bottom":
      pos.set(center.x, center.y - distance, center.z);
      camera.up.set(0, 0, 1);
      break;

    case "reset":
    default:
      pos.set(center.x, center.y + distance * 0.4, center.z + distance);
      camera.up.set(0, 1, 0);
      break;
  }

  controls.target.copy(center);
  camera.position.copy(pos);
  camera.lookAt(center);

  camera.near = Math.max(distance / 100, 0.001);
  camera.far = Math.max(distance * 50, 1000);

  camera.updateProjectionMatrix();
  controls.update();

  console.log("[viewportCameraUtils] setViewPreset OUTPUT", {
  preset,
  cameraPositionAfter: camera.position.toArray(),
  targetAfter: controls.target.toArray(),
  distanceAfter: camera.position.distanceTo(controls.target),
});
}